import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  ContextualMenu,
  Tabs,
  ToggleSwitch,
  type ButtonVariant,
  type ContextualMenuItem,
} from '@enamelai/design-system'
import { Icon as ArrowLeftIcon } from '@enamelai/design-system/icons/Arrow_2_left'
import { Icon as ArrowRightIcon } from '@enamelai/design-system/icons/Arrow_2_right'
import { Icon as RecordIcon } from '@enamelai/design-system/icons/Record'
import { Icon as PauseIcon } from '@enamelai/design-system/icons/Audio_player_pause'
import { Icon as NotesIcon } from '@enamelai/design-system/icons/Notes'
import { Icon as RefreshIcon } from '@enamelai/design-system/icons/Refresh_CCW'
import { Icon as ShieldIcon } from '@enamelai/design-system/icons/Shield'
import { Icon as ShieldOutlineIcon } from '@enamelai/design-system/icons/Shield_outline'
import { Icon as ExpandIcon } from '@enamelai/design-system/icons/Expand_content'
import { Icon as SettingsIcon } from '@enamelai/design-system/icons/Settings'
import CreateExistingChartModal from '@components/pages/Charting/Modals/CreateExistingChartModal'
import CompareChartsModal from '@components/pages/Charting/Modals/CompareChartsModal'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { tabButtons } from '@constants/Charting'
import {
  setDentalUpdated,
  setPerioLocked,
  setPerioUpdated,
  setSelectedDentalChart,
  setSelectedPerioChart,
} from '@store/reducers/charting.reducer'
import ChartingResetChartModal from '@components/pages/Charting/Modals/ChartingResetChartModal.component'
import ChartingAddChartModal from '@components/pages/Charting/Modals/ChartingAddChartModal.component'
import PrintPdfModal from '@components/pages/Charting/Modals/PrintPdfModal'
import {
  CHARTING_LAYER_KEYS,
  type ChartingLayersState,
} from '@utils/default-charting-view.utils'
import { ChartingLayerKey } from '@dataTypes/chartingPreferences.type'
import type { ChartAutoSaveStatus } from '@utils/chart-save.utils'

type TabType = 'home' | 'perio' | 'treatment-plan' | 'history'

const CHARTING_TAB_ITEMS = tabButtons.map((button) => ({
  id: button.value,
  label: button.label,
}))

const TOOLBAR_ICON_SIZE = 16

type ChartingToolbarIconButtonProps = {
  label: string
  disabled?: boolean
  variant?: ButtonVariant
  destructive?: boolean
  onClick?: () => void
  icon: React.ReactNode
}

const toolbarIcon = (
  Icon: React.FC<React.SVGProps<SVGSVGElement>>,
): React.ReactElement => (
  <Icon width={TOOLBAR_ICON_SIZE} height={TOOLBAR_ICON_SIZE} aria-hidden />
)

const ChartingToolbarIconButton = ({
  label,
  disabled = false,
  variant = 'secondary',
  destructive = false,
  onClick,
  icon,
}: ChartingToolbarIconButtonProps): React.ReactElement => (
  <Button
    variant={variant}
    size="sm"
    className="charting-toolbar-icon-button"
    aria-label={label}
    title={label}
    disabled={disabled}
    destructive={destructive}
    leftIcon={icon}
    onClick={onClick}
  />
)

type ChartingOptionsMenuProps = {
  items: ContextualMenuItem[]
  disabled?: boolean
}

const ChartingOptionsMenu = ({
  items,
  disabled = false,
}: ChartingOptionsMenuProps): React.ReactElement => {
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    if (disabled && menuOpen) {
      setMenuOpen(false)
    }
  }, [disabled, menuOpen])

  return (
    <div
      className={[
        'charting-options-menu',
        menuOpen ? 'charting-options-menu--open' : '',
        disabled ? 'charting-options-menu--disabled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      title={disabled ? 'Chart is locked' : 'Chart options'}
      aria-disabled={disabled}
      {...(disabled ? { inert: '' as const } : {})}
    >
      <span className="charting-options-menu__face" aria-hidden="true">
        {toolbarIcon(SettingsIcon)}
      </span>
      <ContextualMenu
        ariaLabel="Chart options"
        items={items}
        open={disabled ? false : menuOpen}
        onOpenChange={(nextOpen): void => {
          if (disabled) {
            return
          }
          setMenuOpen(nextOpen)
        }}
      />
    </div>
  )
}

interface ChartingTopBarProps {
  currentTab: TabType
  onTabChange: (tab: string) => void
  layers: ChartingLayersState
  onLayerChange: (layer: ChartingLayerKey) => void
  chartIndex: number
  onChartToggle: (direction: 'left' | 'right') => void
  isMicOn: boolean
  onMicToggle: () => void
  micDisabled: boolean
  micStatus: string
  isFullscreen: boolean
  setIsFullscreen: React.Dispatch<React.SetStateAction<boolean>>
  onAddChart: () => void
  onSaveChart: () => void
  onResetChart: () => void
  onViewNote: (id: string | null) => void
  clearSelection: () => void
  onAddTreatmentPlan?: (() => void) | null
  isXrayPanelOpen: boolean
  onXrayPanelToggle: () => void
  autoSaveEnabled?: boolean
  autoSaveStatus?: ChartAutoSaveStatus
  canFinishEditing?: boolean
}

const ChartingTopBar: React.FC<ChartingTopBarProps> = ({
  currentTab,
  onTabChange,
  layers,
  onLayerChange,
  chartIndex,
  onChartToggle,
  isMicOn,
  onMicToggle,
  micDisabled,
  micStatus,
  isFullscreen,
  setIsFullscreen,
  onAddChart,
  onSaveChart,
  onResetChart,
  onViewNote,
  clearSelection,
  onAddTreatmentPlan,
  isXrayPanelOpen,
  onXrayPanelToggle,
  autoSaveEnabled = false,
  autoSaveStatus = 'idle',
  canFinishEditing = false,
}) => {
  const dispatch = useAppDispatch()
  const compareCharts = useAppSelector((state) => state.charting.compareCharts)
  const isPerioLocked = useAppSelector((state) => state.charting.isPerioLocked)
  const isPerioTabLocked = currentTab === 'perio' && isPerioLocked

  const isPerioUpdated = useAppSelector(
    (state) => state.charting.isPerioUpdated,
  )
  const isDentalUpdated = useAppSelector(
    (state) => state.charting.isDentalUpdated,
  )
  const selectedChart = useAppSelector((state) =>
    currentTab === 'home'
      ? state.charting.selectedDentalChart
      : state.charting.selectedPerioChart,
  )
  const isUpdated = useAppSelector((state) =>
    currentTab === 'perio'
      ? state.charting.isPerioUpdated
      : state.charting.isDentalUpdated,
  )

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [showCompareModal, setShowCompareModal] = useState(false)
  const [showPDFModal, setShowPDFModal] = useState(false)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [showAddConfirm, setShowAddConfirm] = useState(false)

  const handleLockClick = (): void => {
    dispatch(setPerioLocked(!isPerioLocked))
  }

  const handleRefreshChart = (): void => {
    setShowResetConfirm(true)
  }

  const handleAddChart = (): void => {
    if (currentTab === 'perio' && isPerioUpdated) {
      setShowAddConfirm(true)
      return
    }
    if (currentTab === 'home' && isDentalUpdated) {
      setShowAddConfirm(true)
      return
    }
    onAddChart()
  }

  const chartOptionsItems = useMemo((): ContextualMenuItem[] => {
    const lockMenu = currentTab !== 'treatment-plan' && isPerioTabLocked
    const printItem: ContextualMenuItem = {
      id: 'print-chart',
      label: 'Print chart',
      disabled: lockMenu,
      onSelect: (): void => setShowPDFModal(true),
    }
    const emailItem: ContextualMenuItem = {
      id: 'email-chart',
      label: 'Email chart',
      disabled: true,
    }
    if (currentTab === 'treatment-plan') {
      return [printItem, emailItem]
    }
    return [
      {
        id: 'create-from-existing',
        label: 'Create from an existing chart',
        disabled: lockMenu,
        onSelect: (): void => setShowCreateModal(true),
      },
      {
        id: 'compare-charts',
        label: 'Compare charts',
        disabled: lockMenu,
        onSelect: (): void => setShowCompareModal(true),
      },
      printItem,
      emailItem,
    ]
  }, [currentTab, isPerioTabLocked])

  const isSaveEnabled = (isUpdated || canFinishEditing) && !isPerioTabLocked
  const saveTooltip = isPerioTabLocked
    ? 'Chart is locked'
    : isUpdated
      ? canFinishEditing
        ? 'Save and finish editing'
        : 'Save changes'
      : canFinishEditing
        ? 'Finish editing'
        : 'No changes to save'

  useEffect(() => {
    if (!!compareCharts && !layers.perio) {
      onLayerChange('perio')
    }
    if (!!compareCharts && currentTab !== 'home') {
      onTabChange('home')
    }
  }, [compareCharts])

  return (
    <div className="charting-top-bar">
      <div className="charting-top-bar__start">
        <div className="flex-shrink-0">
          <Tabs
            items={CHARTING_TAB_ITEMS}
            value={currentTab}
            onChange={onTabChange}
          />
        </div>
        {currentTab !== 'history' && <div className="icon-divider" />}
        {currentTab === 'perio' && (
          <div className="d-flex align-items-center gap-1">
            <ChartingToolbarIconButton
              label="Upper Arch"
              variant={chartIndex === 0 ? 'primary' : 'secondary'}
              disabled={isFullscreen}
              icon={toolbarIcon(ArrowLeftIcon)}
              onClick={() => onChartToggle('left')}
            />
            <ChartingToolbarIconButton
              label="Lower Arch"
              variant={chartIndex === 1 ? 'primary' : 'secondary'}
              disabled={isFullscreen}
              icon={toolbarIcon(ArrowRightIcon)}
              onClick={() => onChartToggle('right')}
            />
            <ChartingToolbarIconButton
              label={!isPerioLocked ? 'Lock chart' : 'Unlock chart'}
              disabled={isFullscreen || !isUpdated}
              icon={toolbarIcon(
                !isPerioLocked ? ShieldIcon : ShieldOutlineIcon,
              )}
              onClick={handleLockClick}
            />
          </div>
        )}
        {currentTab === 'home' && (
          <div className="d-flex align-items-center gap-3">
            {CHARTING_LAYER_KEYS.map((layer) => (
              <Checkbox
                key={layer}
                id={`layer-${layer}`}
                label={`${layer.charAt(0).toUpperCase()}${layer.slice(1)}`}
                checked={layers[layer]}
                onChange={() => onLayerChange(layer)}
              />
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="icon-buttons-container">
          {autoSaveEnabled &&
          currentTab !== 'treatment-plan' &&
          currentTab !== 'history' ? (
            <span
              className={`charting-autosave-status-slot charting-autosave-status${
                autoSaveStatus !== 'idle'
                  ? ` charting-autosave-status--${autoSaveStatus}`
                  : ''
              }`}
              aria-live="polite"
            >
              {autoSaveStatus === 'saving'
                ? 'Saving...'
                : autoSaveStatus === 'saved'
                  ? 'Saved'
                  : autoSaveStatus === 'error'
                    ? 'Save failed'
                    : ''}
            </span>
          ) : null}
          {(currentTab === 'home' || currentTab === 'treatment-plan') && (
            <div className="d-inline-flex align-items-center gap-2 me-2">
              <ToggleSwitch
                id="charting-xray-panel-toggle"
                aria-label="Image viewer panel"
                checked={isXrayPanelOpen}
                onChange={(checked): void => {
                  if (checked !== isXrayPanelOpen) {
                    onXrayPanelToggle()
                  }
                }}
              />
              <button
                type="button"
                className="charting-xray-panel-label mb-0 text-nowrap"
                onClick={onXrayPanelToggle}
              >
                Image viewer panel
              </button>
            </div>
          )}
          {currentTab !== 'history' && currentTab !== 'treatment-plan' ? (
            <ChartingToolbarIconButton
              label={
                micDisabled
                  ? currentTab === 'home'
                    ? 'Select an editable Dental chart'
                    : currentTab === 'perio'
                      ? 'Select an editable, unlocked Perio chart'
                      : 'Voice assistant is unavailable'
                  : `Voice assistant: ${micStatus}`
              }
              disabled={micDisabled}
              variant={isMicOn ? 'primary' : 'secondary'}
              destructive={isMicOn}
              icon={toolbarIcon(isMicOn ? PauseIcon : RecordIcon)}
              onClick={onMicToggle}
            />
          ) : null}
          {currentTab !== 'treatment-plan' && currentTab !== 'history' ? (
            <>
              <ChartingToolbarIconButton
                label="Add note"
                disabled={!selectedChart}
                icon={toolbarIcon(NotesIcon)}
                onClick={() => onViewNote(selectedChart?.chart_id ?? '')}
              />
              <div className="icon-divider" />
              <ChartingToolbarIconButton
                label="Refresh"
                disabled={isPerioTabLocked || !isUpdated}
                icon={toolbarIcon(RefreshIcon)}
                onClick={handleRefreshChart}
              />
              <div className="charting-save-button-wrap" title={saveTooltip}>
                <ChartingToolbarIconButton
                  label={saveTooltip}
                  variant={isSaveEnabled ? 'primary' : 'secondary'}
                  disabled={!isSaveEnabled}
                  icon={<i className="mgc_save_2_line" aria-hidden />}
                  onClick={onSaveChart}
                />
                {isUpdated ? (
                  <span className="charting-save-button-wrap__badge" />
                ) : null}
              </div>
              <div className="icon-divider" />
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddChart}
                disabled={isPerioTabLocked}
              >
                Add chart
              </Button>
            </>
          ) : null}

          {currentTab !== 'treatment-plan' && currentTab !== 'history' ? (
            <ChartingOptionsMenu
              items={chartOptionsItems}
              disabled={isPerioTabLocked}
            />
          ) : currentTab === 'treatment-plan' ? (
            <>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  if (onAddTreatmentPlan) {
                    onAddTreatmentPlan()
                  }
                }}
              >
                Add plan
              </Button>
              <ChartingOptionsMenu items={chartOptionsItems} />
            </>
          ) : null}

          {currentTab !== 'treatment-plan' && currentTab !== 'history' && (
            <ChartingToolbarIconButton
              label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              icon={toolbarIcon(ExpandIcon)}
              onClick={() => setIsFullscreen((prev) => !prev)}
            />
          )}
        </div>
      </div>
      <CreateExistingChartModal
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false)
          setSearchTerm('')
          clearSelection()
          dispatch(
            currentTab === 'perio'
              ? setSelectedPerioChart(null)
              : setSelectedDentalChart(null),
          )
        }}
        chartType={currentTab === 'perio' ? 'perio' : 'dental'}
        searchTerm={searchTerm}
        onSearchChange={(val) => setSearchTerm(val)}
      />

      <CompareChartsModal
        show={showCompareModal}
        onHide={() => setShowCompareModal(false)}
      />

      <PrintPdfModal
        show={showPDFModal}
        onHide={() => setShowPDFModal(false)}
      />

      <ChartingResetChartModal
        show={showResetConfirm}
        onCancel={() => setShowResetConfirm(false)}
        onReset={() => {
          setShowResetConfirm(false)
          onResetChart()
        }}
      />

      <ChartingAddChartModal
        show={showAddConfirm}
        onCancel={() => setShowAddConfirm(false)}
        onDiscardAndContinue={() => {
          setShowAddConfirm(false)
          onAddChart()
          if (currentTab === 'perio') {
            dispatch(setPerioUpdated(false))
          }
          if (currentTab === 'home') {
            dispatch(setDentalUpdated(false))
          }
        }}
      />
    </div>
  )
}

export default ChartingTopBar
